import type React from 'react'
import { forwardRef, type Ref, useRef, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import { DSInputType, Input } from 'components'

import * as S from './styles'

export interface AutocompleteProps<T> {
  placeHolder?: string
  label?: string
  data: Array<T>
  required?: boolean
  disabled?: boolean
  useCodeOnDescription?: boolean
  onInputChange?: () => void
  objId: keyof T
  objDescription: keyof T
  input: string
  setInput: React.Dispatch<React.SetStateAction<string>>
  setOptionSelected?: (e?: T) => void
  error?: string | null
  inputId?: string
  browserAutocomplete?: 'on' | 'off'
  inputAdornment?: React.ReactNode
  resetOnBlur?: boolean
}

const AutocompleteInput = <T,>(
  {
    placeHolder,
    label,
    data,
    required,
    disabled,
    useCodeOnDescription,
    setOptionSelected = () => {
      return
    },
    error,
    objId,
    objDescription,
    input,
    setInput,
    inputId,
    browserAutocomplete,
    onInputChange = () => {
      return
    },
    inputAdornment,
    resetOnBlur = false,
  }: AutocompleteProps<T>,
  ref: Ref<HTMLInputElement>,
) => {
  const [storedOption, setStoredOption] = useState<T | null>(null)
  const [filteredSuggestionsObj, setFilteredSuggestionsObj] = useState<typeof data>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const SearchContentRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(SearchContentRef, () => setShowSuggestions(false))

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = event.target.value
    const unLinkedObj: typeof data = data.filter(
      (item) =>
        String(item[objId]).toLowerCase().indexOf(userInput.toLowerCase()) > -1 ||
        String(item[objDescription]).toLowerCase().indexOf(userInput.toLowerCase()) > -1,
    )
    setInput(event.target.value)
    onInputChange()
    setFilteredSuggestionsObj(unLinkedObj)
    setShowSuggestions(true)
  }

  const onFocus = () => {
    const unLinkedObj: typeof data = data
    onInputChange()
    setFilteredSuggestionsObj(unLinkedObj)
    setShowSuggestions(true)
  }

  const onBlur = () => {
    if (resetOnBlur) {
      if (storedOption) {
        setInput(String(storedOption[objDescription]))
      } else {
        setInput('')
      }
    }
  }

  return (
    <S.Wrapper>
      <Input
        browserAutocomplete={browserAutocomplete}
        design={DSInputType.SEARCH_INPUT}
        disabled={disabled}
        error={error}
        InputProps={{
          endAdornment: <ExpandMoreIcon color="primary" />,
          fullWidth: true,
          placeholder: placeHolder,
        }}
        id={inputId || String(objId)}
        label={label}
        labelAdornment={inputAdornment}
        onBlur={onBlur}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event)}
        onFocus={(event) => {
          onFocus()
          event.target.select()
          setShowSuggestions(true)
        }}
        ref={ref}
        required={required}
        value={input}
      />
      {showSuggestions && (
        <S.SearchResults>
          <S.SearchContent ref={SearchContentRef}>
            {!filteredSuggestionsObj.length && (
              <S.SearchItem>Nenhuma opção encontrada</S.SearchItem>
            )}
            {filteredSuggestionsObj.map((item, index) => (
              <S.SearchItem
                key={String(`${item[objId]}-${item[objDescription]}-${index}`)}
                onClick={(event) => {
                  setStoredOption(item)
                  setOptionSelected(item)
                  setFilteredSuggestionsObj([])
                  setInput((event.target as HTMLElement).innerText)
                  setShowSuggestions(false)
                }}
              >
                {useCodeOnDescription
                  ? `${item[objId]} - ${item[objDescription]}`
                  : `${item[objDescription]}`}
              </S.SearchItem>
            ))}
          </S.SearchContent>
        </S.SearchResults>
      )}
    </S.Wrapper>
  )
}

export const Autocomplete = forwardRef(AutocompleteInput) as unknown as <T>(
  props: AutocompleteProps<T> & { ref: Ref<HTMLInputElement> },
) => React.ReactElement
